/**
 * DocSync — the client half of two-way sync, shared by the web app, the
 * scripted test client and (ported 1:1) Swift CenterlineCore.
 *
 *   confirmed  = server snapshot at confirmedSeq (only server ops applied)
 *   pending    = local ops not yet in the server log, in order
 *   view       = confirmed + pending (what the user sees and edits)
 *
 * Local edits apply to the view at once and queue for sending (one request
 * in flight; resends are safe because the server dedups by opId). Server
 * ops — other people's and our own echoes — apply to `confirmed`, and the
 * view is rebuilt by replaying what is still pending on top (a rebase). A
 * pending op that no longer applies is dropped and reported as a conflict.
 *
 * Undo/redo are ops too: undo applies the stored inverse as a new edit, so it
 * syncs like any other change and never rewinds anyone else's work.
 */
import { applyOp, type Operation } from "./ops.js";
import type { CenterlineDocument } from "./types.js";

export interface LogEntry {
  seq: number; opId: string; clientId: string; actor: string; baseSeq: number; at: string; receivedAt: string; op: Operation;
}
export type OpResult =
  | { opId: string; status: "applied"; seq: number; overwrote?: string[] }
  | { opId: string; status: "duplicate"; seq: number }
  | { opId: string; status: "rejected"; code: string; message: string };
export interface SubmitResponse { headSeq: number; results: OpResult[]; ops: LogEntry[]; more: boolean }
export interface PendingOp { opId: string; op: Operation; at: string }

export class SyncHttpError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) { super(message); this.name = "SyncHttpError"; }
}
/** Thrown by a transport when the server cannot be reached (offline). */
export class SyncOfflineError extends Error {
  constructor(message = "Server unreachable") { super(message); this.name = "SyncOfflineError"; }
}

export interface SyncTransport {
  submit(req: { baseSeq: number; clientId: string; actor: string; ops: PendingOp[] }): Promise<SubmitResponse>;
  opsSince(since: number): Promise<{ headSeq: number; ops: LogEntry[]; more: boolean }>;
  snapshot(): Promise<{ document: CenterlineDocument; headSeq: number }>;
}

export type SyncStatus = "synced" | "sending" | "offline" | "error";

export type SyncEvent =
  | { type: "change"; source: "local" | "remote" | "rebase" }
  | { type: "status"; status: SyncStatus; pending: number }
  | { type: "rejected"; opId: string; op: Operation; code: string; message: string }
  | { type: "overwrote"; opId: string; ids: string[] }
  | { type: "remote"; entries: LogEntry[] }
  | { type: "reset" };

export interface DocSyncOptions {
  clientId: string;
  actor: string;
  transport: SyncTransport;
  newOpId: () => string;
  now?: () => string;
  /** Restored offline queue (e.g. from localStorage / the Mac's queue file). */
  pending?: PendingOp[];
  onEvent?: (e: SyncEvent) => void;
  /** Persist the queue whenever it changes. */
  onPendingChange?: (pending: readonly PendingOp[]) => void;
  /** Retry delay after a failed send (ms); doubles up to 30 s. */
  retryMs?: number;
  schedule?: (fn: () => void, ms: number) => unknown;
  /** Coalescing delay before a send (ms). */
  flushDelayMs?: number;
  maxUndo?: number;
}

export class DocSync {
  confirmed: CenterlineDocument;
  confirmedSeq: number;
  view: CenterlineDocument;
  pending: PendingOp[];
  status: SyncStatus = "synced";
  private undoStack: Operation[] = [];
  private redoStack: Operation[] = [];
  private inFlight: Promise<void> | null = null;
  private flushTimer: unknown = null;
  private retryDelay: number;
  private retryTimer: unknown = null;
  private closed = false;
  private readonly o: Required<Omit<DocSyncOptions, "pending" | "onEvent" | "onPendingChange">> & Pick<DocSyncOptions, "onEvent" | "onPendingChange">;

  constructor(document: CenterlineDocument, headSeq: number, options: DocSyncOptions) {
    this.o = {
      now: () => new Date().toISOString(), retryMs: 1000, schedule: (fn, ms) => setTimeout(fn, ms), flushDelayMs: 0, maxUndo: 200,
      ...options,
    } as DocSync["o"];
    this.retryDelay = this.o.retryMs;
    this.confirmed = document;
    this.confirmedSeq = headSeq;
    this.pending = [...(options.pending ?? [])];
    this.view = document;
    if (this.pending.length) { this.rebuild(); this.scheduleFlush(); }
  }

  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }

  close(): void { this.closed = true; }

  private emit(e: SyncEvent): void { this.o.onEvent?.(e); }
  private setStatus(s: SyncStatus): void {
    this.status = s;
    this.emit({ type: "status", status: s, pending: this.pending.length });
  }
  private pendingChanged(): void { this.o.onPendingChange?.(this.pending); }

  /** Applies a local edit. Throws OpError (nothing changes) if the edit is invalid. */
  apply(op: Operation, history: "record" | "undo" | "redo" | "none" = "record"): Operation {
    const { doc, inverse } = applyOp(this.view, op);
    this.view = doc;
    this.pending.push({ opId: this.o.newOpId(), op, at: this.o.now() });
    if (history === "record") { this.undoStack.push(inverse); this.redoStack = []; }
    else if (history === "undo") this.redoStack.push(inverse);
    else if (history === "redo") this.undoStack.push(inverse);
    if (this.undoStack.length > this.o.maxUndo) this.undoStack.shift();
    this.pendingChanged();
    this.emit({ type: "change", source: "local" });
    this.scheduleFlush();
    return inverse;
  }

  /** Returns false when there is nothing to undo or the change no longer applies (someone else removed it). */
  undo(): boolean { return this.step(this.undoStack, "undo"); }
  redo(): boolean { return this.step(this.redoStack, "redo"); }
  private step(stack: Operation[], kind: "undo" | "redo"): boolean {
    while (stack.length) {
      const op = stack.pop()!;
      try { this.apply(op, kind); return true; } catch { /* target gone; try the next entry */ }
    }
    return false;
  }

  /** Server ops (from SSE or a submit response). Gaps trigger a catch-up fetch. */
  receive(entries: readonly LogEntry[]): void {
    const fresh = entries.filter((e) => e.seq > this.confirmedSeq).sort((a, b) => a.seq - b.seq);
    if (!fresh.length) return;
    const applied: LogEntry[] = [];
    let gap = false;
    for (const e of fresh) {
      if (e.seq !== this.confirmedSeq + 1) { gap = true; break; }
      try {
        this.confirmed = applyOp(this.confirmed, e.op).doc;
      } catch {
        // Our copy disagrees with the server's: reload the snapshot.
        void this.resync();
        return;
      }
      this.confirmedSeq = e.seq;
      applied.push(e);
      const mine = this.pending.findIndex((p) => p.opId === e.opId);
      if (mine >= 0) this.pending.splice(mine, 1);
    }
    if (applied.length) {
      const remote = applied.some((e) => e.clientId !== this.o.clientId);
      this.rebuild();
      this.pendingChanged();
      if (remote) this.emit({ type: "remote", entries: applied.filter((e) => e.clientId !== this.o.clientId) });
      this.emit({ type: "change", source: remote ? "remote" : "rebase" });
    }
    if (gap) void this.catchUp();
  }

  /** view = confirmed + pending; drops pending ops that no longer apply. */
  private rebuild(): void {
    let doc = this.confirmed;
    const keep: PendingOp[] = [];
    for (const p of this.pending) {
      try { doc = applyOp(doc, p.op).doc; keep.push(p); } catch (err) {
        this.emit({ type: "rejected", opId: p.opId, op: p.op, code: (err as { code?: string }).code ?? "conflict", message: (err as Error).message });
      }
    }
    if (keep.length !== this.pending.length) this.pending = keep;
    this.view = doc;
  }

  private scheduleFlush(): void {
    if (this.closed || this.flushTimer || this.inFlight) return;
    this.flushTimer = this.o.schedule(() => { this.flushTimer = null; void this.flush(); }, this.o.flushDelayMs);
  }

  /** Sends everything pending; resolves when the queue is empty or the server is unreachable. */
  async flush(): Promise<void> {
    if (this.closed) return;
    if (this.inFlight) return this.inFlight;
    if (!this.pending.length) { if (this.status !== "synced") this.setStatus("synced"); return; }
    this.inFlight = this.sendOnce().finally(() => { this.inFlight = null; });
    await this.inFlight;
    if (this.pending.length && this.status === "sending") return this.flush();
  }

  private async sendOnce(): Promise<void> {
    const batch = this.pending.slice(0, 200);
    this.setStatus("sending");
    let resp: SubmitResponse;
    try {
      resp = await this.o.transport.submit({ baseSeq: this.confirmedSeq, clientId: this.o.clientId, actor: this.o.actor, ops: batch });
    } catch (err) {
      if (err instanceof SyncHttpError && err.code === "snapshot_required") { await this.resync(); this.setStatus("sending"); return; }
      const offline = err instanceof SyncOfflineError || !(err instanceof SyncHttpError) || err.status >= 500;
      this.setStatus(offline ? "offline" : "error");
      if (!offline && err instanceof SyncHttpError && err.status >= 400 && err.status < 500) {
        // The server will never take this batch as sent: drop it and say so.
        for (const p of batch) this.emit({ type: "rejected", opId: p.opId, op: p.op, code: err.code, message: err.message });
        const ids = new Set(batch.map((p) => p.opId));
        this.pending = this.pending.filter((p) => !ids.has(p.opId));
        this.rebuild(); this.pendingChanged(); this.emit({ type: "change", source: "rebase" });
        return;
      }
      this.scheduleRetry();
      return;
    }
    this.retryDelay = this.o.retryMs;
    this.receive(resp.ops);
    let dropped = false;
    for (const r of resp.results) {
      if (r.status === "rejected") {
        const i = this.pending.findIndex((p) => p.opId === r.opId);
        if (i >= 0) { const [p] = this.pending.splice(i, 1); dropped = true; this.emit({ type: "rejected", opId: r.opId, op: p!.op, code: r.code, message: r.message }); }
      } else {
        if (r.status === "applied" && r.overwrote?.length) this.emit({ type: "overwrote", opId: r.opId, ids: r.overwrote });
        if (r.seq <= this.confirmedSeq) {
          const i = this.pending.findIndex((p) => p.opId === r.opId);
          if (i >= 0) { this.pending.splice(i, 1); dropped = true; }
        }
      }
    }
    if (dropped) { this.rebuild(); this.pendingChanged(); this.emit({ type: "change", source: "rebase" }); }
    if (resp.more) await this.catchUp();
    if (!this.pending.length) this.setStatus("synced");
  }

  private scheduleRetry(): void {
    if (this.closed || this.retryTimer) return;
    const delay = this.retryDelay;
    this.retryDelay = Math.min(30_000, this.retryDelay * 2);
    this.retryTimer = this.o.schedule(() => { this.retryTimer = null; void this.flush(); }, delay);
  }

  /** Call when connectivity returns (e.g. `online` event) to replay the queue now. */
  retryNow(): Promise<void> {
    this.retryDelay = this.o.retryMs;
    return this.flush();
  }

  async catchUp(): Promise<void> {
    for (let i = 0; i < 50; i++) {
      let r;
      try { r = await this.o.transport.opsSince(this.confirmedSeq); } catch (err) {
        if (err instanceof SyncHttpError && err.code === "snapshot_required") return this.resync();
        return;
      }
      this.receive(r.ops);
      if (!r.more) return;
    }
  }

  /** Reloads the server snapshot and replays pending local ops on top. */
  async resync(): Promise<void> {
    const snap = await this.o.transport.snapshot();
    this.confirmed = snap.document;
    this.confirmedSeq = snap.headSeq;
    this.rebuild();
    this.pendingChanged();
    this.emit({ type: "reset" });
    this.emit({ type: "change", source: "remote" });
  }
}

/** Uppercase UUID v4 — the Mac's UUID().uuidString form, so ids compare equal everywhere. */
export function newItemId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID().toUpperCase();
  const b = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  b[6] = (b[6]! & 0x0f) | 0x40; b[8] = (b[8]! & 0x3f) | 0x80;
  const h = b.map((x) => x.toString(16).padStart(2, "0")).join("").toUpperCase();
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
